import { useMemo, useCallback, useContext } from 'react'
import {
    ApiShopperLandingPageLink,
    SelectedOption,
    getOptionsFromVariant,
    getActiveVariantAndImage,
    isOptionAvailable,
    findFirstAvailableVariantWithOption,
    buildOptionKeyMap,
} from '@storefronts/common-types'
import {
    useProductData,
    type ProductData,
} from '../../landing-page/products/ProductContent'
import { ViddyLPContext } from '../../../context/viddyLPContext'
import { findProductGridColorSwatchesFromOptionsList } from './ProductGridColorSwatches'
import { ActiveLPLinkOptions } from '../../../context/PageContextProvider'

export interface UseProductGridVariantSelectionProps {
    lpLink: ApiShopperLandingPageLink
    /** When true, shows swatch UI and wires selection handlers. */
    showSwatches?: boolean
    /** When true will always try to select an active variant by default (if the default variant is unavailable). showSwatches=true forces this to true.*/
    preferAvailable?: boolean
}

/** Grid selection + {@link ProductData} from a single `useProductData` (always runs). */
export type UseProductGridVariantSelectionReturn = ProductData & {
    selectedOptions: SelectedOption[]
    handleOptionSelect: (optionName: string, optionValue: string) => void
}

export const useProductGridVariantSelection = ({
    lpLink,
    showSwatches = true,
    preferAvailable = false,
}: UseProductGridVariantSelectionProps): UseProductGridVariantSelectionReturn => {
    const variants = useMemo(
        () => (showSwatches ? lpLink.variantInfo || [] : []),
        [showSwatches, lpLink.variantInfo],
    )
    const options = useMemo(
        () => (showSwatches ? lpLink.optionInfo || [] : []),
        [showSwatches, lpLink.optionInfo],
    )
    const optionKeyMap = useMemo(() => buildOptionKeyMap(options), [options])
    const { landingPage } = useContext(ViddyLPContext)

    const useProductDataOptions = useMemo(() => {
        if (!showSwatches && !preferAvailable) return undefined
        const activeLpLinkOptions: ActiveLPLinkOptions = {
            preferAvailable: true,
        }
        if (showSwatches) {
            const optionInfo = lpLink.optionInfo || []
            const optionName =
                findProductGridColorSwatchesFromOptionsList(optionInfo)?.name ??
                optionInfo[0]?.name ??
                undefined
            if (optionName) activeLpLinkOptions.preferredOptionName = optionName
        }
        return activeLpLinkOptions
    }, [showSwatches, preferAvailable, lpLink.optionInfo])

    const {
        activeVariant,
        setActiveVariant,
        reviewsRef,
        price,
        comparePrice,
        discountPercentage,
        selectedSellingPlanId,
        setSelectedSellingPlanId,
    } = useProductData(lpLink, landingPage, useProductDataOptions)

    const selectedOptions = useMemo((): SelectedOption[] => {
        if (!variants.length || !activeVariant) return []
        return getOptionsFromVariant(activeVariant, options, optionKeyMap)
    }, [activeVariant, variants, options, optionKeyMap])

    const handleOptionSelect = useCallback(
        (optionName: string, optionValue: string) => {
            if (!variants.length) return

            const isAvailable = isOptionAvailable({
                variants,
                optionName,
                optionValue,
                selectedOptions,
                optionKeyMap,
            })

            if (isAvailable) {
                const newSelection = selectedOptions.map((opt) =>
                    opt.name === optionName
                        ? { ...opt, value: optionValue }
                        : opt,
                )
                const { activeVariant: next } = getActiveVariantAndImage(
                    variants,
                    newSelection,
                    optionKeyMap,
                    { fallbackImageUrl: lpLink.imageUrl },
                )
                setActiveVariant(next)
            } else {
                const newVariant = findFirstAvailableVariantWithOption({
                    variants,
                    optionName,
                    optionValue,
                    optionKeyMap,
                })
                if (newVariant) {
                    setActiveVariant(newVariant)
                }
            }
        },
        [
            variants,
            selectedOptions,
            setActiveVariant,
            lpLink.imageUrl,
            optionKeyMap,
        ],
    )

    return {
        selectedOptions,
        handleOptionSelect,
        activeVariant,
        setActiveVariant,
        reviewsRef,
        price,
        comparePrice,
        discountPercentage,
        selectedSellingPlanId,
        setSelectedSellingPlanId,
    }
}
